// Wallet-free public chain read for a video — the entry point to Decision 1
// of docs/plans/2026-08-13-verification-integration-closeout.md: the API
// only points at where to look, the chain is the authority on what is true.
//
// Reads must work with no wallet and no account, so this derives PDAs from
// the AUTHORITY THE API RECORDED (Video.publication.chain.authority), never
// from a connected wallet — a public viewer has none, and even a logged-in
// admin's wallet need not be the video's publisher. It reads through
// useForumVideo (frontend/src/solana/forum/useForumVideo.ts), which is
// itself wallet-free: it only needs a connection, via useProgramReader, and
// never useProgram or a bare BorshAccountsCoder (see that hook's and
// useProgramReader's doc comments for why).
import { Buffer } from 'buffer'
import { useMemo } from 'react'
import { useForumVideo } from '../solana/forum/index.js'
import type { VideoAccount } from '../solana/forum/index.js'
import type { Video } from '../types.js'

export type AnchorState = 'unanchored' | 'published' | 'sealed'

export interface ChainRecord {
  rootHash: string // lowercase hex
  state: AnchorState
  revision: number
  publishedAt: Date
  sealedAt: Date | null // null while unsealed; seal_video always sets it
}

const toHex = (bytes: number[] | Uint8Array): string => Buffer.from(bytes).toString('hex')

// Anchor decodes the Rust enum's variants as `{ published: {} }` / `{ sealed: {} }`.
// Same pattern as chainStateOf in ChainAnchorBlock.tsx — the program has
// exactly these two variants, so "not explicitly sealed" is always "published".
const isSealed = (state: VideoAccount['state']): boolean => 'sealed' in (state as object)

/** The subset of a decoded Video account computeChainRecord actually reads.
 * Narrower than VideoAccount on purpose: a test fixture only has to supply
 * five fields, not fabricate every account field the program happens to
 * store. A real VideoAccount satisfies this structurally, no cast needed. */
export type ChainRecordSource = Pick<VideoAccount, 'rootHash' | 'revision' | 'publishedAt' | 'sealedAt' | 'state'>

/**
 * Computes a ChainRecord from a decoded on-chain Video account. Pure and
 * exported so the is_sealed rule is testable without a validator, a wallet,
 * or a React render.
 *
 * Mirrors Video::is_sealed in chain/programs/forum/src/lib.rs exactly:
 *
 *   pub fn is_sealed(&self) -> bool {
 *       self.state == VideoState::Sealed
 *   }
 *
 * Sealed is the account's own state and nothing else. Publishing and sealing
 * are separate, explicit acts: there is no correction window, so no clock is
 * read here and a published video stays correctable until its publisher
 * sends seal_video.
 */
export function computeChainRecord(account: ChainRecordSource): ChainRecord {
  const sealedAtSeconds = account.sealedAt.toNumber()
  return {
    rootHash: toHex(account.rootHash),
    state: isSealed(account.state) ? 'sealed' : 'published',
    revision: account.revision,
    publishedAt: new Date(account.publishedAt.toNumber() * 1000),
    sealedAt: sealedAtSeconds > 0 ? new Date(sealedAtSeconds * 1000) : null,
  }
}

export interface ChainReadState {
  chain: ChainRecord | null // null while loading, or when the video was never anchored
  loading: boolean
  error: Error | null
}

/**
 * Reads a video's chain record with no wallet and no account. This is what
 * lets `PublicVideo.tsx` and `EmbedVideo.tsx` show a verification badge to a
 * visitor who has never connected a wallet.
 */
export function useChainRecord(video: Video): ChainReadState {
  const authority = video.publication?.chain?.authority ?? null
  const { video: account, loading, error } = useForumVideo(video.id, authority)

  const chain = useMemo(() => (account ? computeChainRecord(account) : null), [account])

  return { chain, loading, error }
}
